import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Camera, ArrowRight, Zap, ChevronUp, ChevronDown, X, CheckCircle2 } from 'lucide-react';
import ShowcaseIDCard from '../shared/ShowcaseIDCard';
import useGuestTracking from '../../hooks/useGuestTracking';

const SkipPage = () => {
  useGuestTracking();
  // Guest Traffic Tracking
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeletedToast, setShowDeletedToast] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('deleted') === '1') {
      setShowDeletedToast(true);
      // URL bereinigen damit der Toast nicht beim Refresh wiederkommt
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      const t = setTimeout(() => setShowDeletedToast(false), 6000);
      return () => clearTimeout(t);
    }
  }, [location.search]);
  const [showcaseData, setShowcaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [previewLightbox, setPreviewLightbox] = useState(null); // Lightbox für Preview-Bilder
  const [showAllCards, setShowAllCards] = useState(true); // Galerie direkt offen für Skip-Page

  // Preview-Bilder — lokal optimiert (WebP, ~30 KB statt 2.5 MB) für zuverlässiges Laden
  const previewImages = [
    '/landing-previews/preview1.webp',
    '/landing-previews/preview2.webp',
    '/landing-previews/preview3.webp',
    '/landing-previews/preview4.webp',
  ];
  const previewImagesFallback = [
    '/landing-previews/preview1.jpg',
    '/landing-previews/preview2.jpg',
    '/landing-previews/preview3.jpg',
    '/landing-previews/preview4.jpg',
  ];

  // Scroll Detection für Scroll-to-Top Button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // iOS Overscroll Fix (nur horizontal, vertical scroll nicht blocken)
  useEffect(() => {
    document.body.style.overscrollBehaviorX = 'none';
    return () => {
      document.body.style.overscrollBehaviorX = '';
    };
  }, []);

  // Lade Julia's Showcase-Daten
  useEffect(() => {
    loadShowcaseData();
  }, []);

  // Keyboard Navigation für Lightbox (Links/Rechts Pfeiltasten)
  useEffect(() => {
    if (previewLightbox === null) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && previewLightbox > 0) {
        setPreviewLightbox(previewLightbox - 1);
      } else if (e.key === 'ArrowRight' && previewLightbox < previewImages.length - 1) {
        setPreviewLightbox(previewLightbox + 1);
      } else if (e.key === 'Escape') {
        setPreviewLightbox(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewLightbox, previewImages.length]);


  const loadShowcaseData = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1500, 3000, 5000]; // ms — exponential backoff

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/showcase/julia`);

      if (res.ok) {
        const data = await res.json();
        setShowcaseData(data);
        setError(null);
        setLoading(false);
      } else if (retryCount < MAX_RETRIES) {
        // Server-Fehler (502/503/500) — Backend evtl. gerade neu gestartet
        console.warn(`Showcase load attempt ${retryCount + 1} failed (HTTP ${res.status}), retrying in ${RETRY_DELAYS[retryCount]}ms...`);
        setTimeout(() => loadShowcaseData(retryCount + 1), RETRY_DELAYS[retryCount]);
      } else {
        throw new Error('Showcase-Daten konnten nicht geladen werden');
      }
    } catch (err) {
      if (retryCount < MAX_RETRIES && !err.message.includes('konnten nicht geladen')) {
        // Netzwerk-Fehler (fetch failed) — retry
        console.warn(`Showcase load attempt ${retryCount + 1} failed (network), retrying in ${RETRY_DELAYS[retryCount]}ms...`);
        setTimeout(() => loadShowcaseData(retryCount + 1), RETRY_DELAYS[retryCount]);
      } else {
        console.error('Error loading showcase:', err);
        setError(err.message);
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-black grain-texture">

      {showDeletedToast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] bg-emerald-600 text-white px-6 py-4 rounded-xl shadow-2xl border border-emerald-400/60 flex items-center gap-3 animate-fade-in max-w-md">
          <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
          <div>
            <div className="font-bold text-sm">Dein Account wurde gelöscht</div>
            <div className="text-xs text-white/80">Alle deine Daten, Avatare und Bilder wurden entfernt.</div>
          </div>
          <button onClick={() => setShowDeletedToast(false)} className="ml-2 text-white/80 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Compact Header mit Logo */}
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
              <Camera className="w-6 h-6 text-black" />
            </div>
            <h1 className="text-2xl font-bold text-white">
              <span className="text-white">SNAPSHOT</span>
            </h1>
          </div>
          <Link 
            to="/welcome"
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-[#10B981]/50 rounded-lg text-white text-sm font-medium transition-all"
          >
            <span>Anmelden</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
      
      {/* Kompakter Hero */}
      <div className="relative py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Erstelle deinen fotorealistischen{' '}
            <span className="inline-block bg-gradient-to-r from-[#10B981] to-[#059669] bg-clip-text text-transparent relative">
              KI-Avatar
              <span className="absolute inset-0 bg-gradient-to-r from-[#10B981]/20 to-[#059669]/20 blur-xl -z-10"></span>
            </span>
            .<br/>
            Keine Schablonen. Keine Limits.
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-2 max-w-2xl mx-auto leading-relaxed">
            Erstelle deinen digitalen Zwilling.<br/>
            Generiere Bilder und Videos in jeder Szene.
          </p>
          <p className="text-sm text-[#10B981]/70 italic">
            Rohe KI-Power für deine Ideen.
          </p>
          
          {/* Mini-Preview Bildstreifen — NUR Mobile (auf Desktop sind die Bilder
              flankierend zur CTA-Box, siehe weiter unten). */}
          <div className="mt-6 mb-2 md:hidden">
            <div
              className="flex gap-3 overflow-x-auto scrollbar-hide max-w-2xl mx-auto px-4"
              style={{ overscrollBehavior: 'contain', touchAction: 'pan-x' }}
            >
              {previewImages.map((imgUrl, index) => (
                <div
                  key={index}
                  onClick={() => setPreviewLightbox(index)}
                  className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden cursor-pointer hover:scale-105 hover:shadow-xl hover:shadow-[#10B981]/30 transition-all border-2 border-[#10B981]/30 hover:border-[#10B981]/60"
                >
                  <picture>
                    <source srcSet={imgUrl} type="image/webp" />
                    <img
                      src={previewImagesFallback[index]}
                      alt={`Preview ${index + 1}`}
                      loading="eager"
                      className="w-full h-full object-cover"
                      style={{ touchAction: 'auto', pointerEvents: 'auto' }}
                    />
                  </picture>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA-Bereich mit flankierenden Preview-Karten (Desktop) */}
      <div className="max-w-6xl mx-auto px-4 mb-6">
        <div className="flex items-stretch justify-center gap-4 lg:gap-6">

          {/* Preview 1 + 2 vertikal links — nur ab md sichtbar */}
          <div className="hidden md:flex flex-col gap-4 justify-center">
            {[0, 1].map((index) => (
              <div
                key={index}
                onClick={() => setPreviewLightbox(index)}
                className="w-32 h-32 lg:w-40 lg:h-40 rounded-xl overflow-hidden cursor-pointer hover:scale-105 hover:shadow-xl hover:shadow-[#10B981]/30 transition-all border-2 border-[#10B981]/30 hover:border-[#10B981]/60"
              >
                <picture>
                  <source srcSet={previewImages[index]} type="image/webp" />
                  <img
                    src={previewImagesFallback[index]}
                    alt={`Preview ${index + 1}`}
                    loading="eager"
                    className="w-full h-full object-cover"
                  />
                </picture>
              </div>
            ))}
          </div>

          {/* CTA-Box (mittig) */}
          <div className="relative w-full max-w-md">
            <div className="absolute inset-0 bg-[#10B981] opacity-20 blur-2xl rounded-2xl pointer-events-none"></div>

            <div className="relative overflow-hidden bg-gradient-to-br from-black via-[#10B981]/10 to-black border-2 border-[#10B981]/50 rounded-2xl p-6 sm:p-7 text-center glow-green-soft">
              {/* Rotierende Sonnenstrahlen — Hintergrund-Layer in der Box */}
              <div
                className="pointer-events-none absolute inset-0 -z-0"
                aria-hidden="true"
                style={{
                  background: 'conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(16,185,129,0.18) 6deg, transparent 12deg, transparent 24deg, rgba(16,185,129,0.10) 30deg, transparent 36deg, transparent 60deg, rgba(16,185,129,0.18) 66deg, transparent 72deg, transparent 90deg, rgba(16,185,129,0.10) 96deg, transparent 102deg, transparent 120deg, rgba(16,185,129,0.18) 126deg, transparent 132deg, transparent 156deg, rgba(16,185,129,0.10) 162deg, transparent 168deg, transparent 186deg, rgba(16,185,129,0.18) 192deg, transparent 198deg, transparent 222deg, rgba(16,185,129,0.10) 228deg, transparent 234deg, transparent 252deg, rgba(16,185,129,0.18) 258deg, transparent 264deg, transparent 288deg, rgba(16,185,129,0.10) 294deg, transparent 300deg, transparent 324deg, rgba(16,185,129,0.18) 330deg, transparent 336deg, transparent 360deg)',
                  WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 0%, black 35%, transparent 75%)',
                  maskImage: 'radial-gradient(circle at 50% 50%, black 0%, black 35%, transparent 75%)',
                  animation: 'spin 60s linear infinite',
                  transformOrigin: 'center',
                }}
              ></div>

              {/* Inhalt liegt über dem Strahlen-Layer */}
              <div className="relative z-10">
              {/* Eyebrow Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 bg-[#10B981]/15 border border-[#10B981]/40 rounded-full">
                <span className="text-[#10B981] text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase">✨ Limitiertes Angebot ✨</span>
              </div>

              {/* Headline */}
              <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight mb-2 leading-tight">
                Dein fotorealistischer<br/>
                <span className="text-[#10B981] glow-green">KI-Avatar</span>
              </h3>

              {/* Inkl.-Liste */}
              <p className="text-white/85 text-sm sm:text-base font-medium mb-3 leading-relaxed">
                Avatar + erstes Bild + erstes Video<br/>
                <span className="text-[#10B981] font-black text-lg">KOSTENLOS</span>
              </p>

              {/* Preis-Linie — auffälliger: Normalpreis durchgestrichen klein, "heute 0 €" als Highlight-Pille */}
              <div className="flex items-center justify-center gap-3 mb-5">
                <span className="text-white/50 text-xs sm:text-sm line-through">
                  Normalpreis 10&nbsp;€
                </span>
                <span className="px-3 py-1 bg-[#10B981] text-black rounded-full font-black text-sm sm:text-base tracking-wide shadow-lg shadow-[#10B981]/40">
                  heute 0&nbsp;€
                </span>
              </div>

              {/* Big CTA Button */}
              <Link
                to="/welcome"
                className="block w-full px-6 py-4 bg-[#10B981] text-black rounded-xl font-black text-base sm:text-lg uppercase tracking-wide hover:bg-[#059669] transition-all duration-300 glow-green pulse-glow shine-effect shadow-2xl"
                data-testid="cta-free-avatar-button"
              >
                Jetzt kostenlosen Avatar sichern
              </Link>

              {/* Verknappung */}
              <p className="mt-3 text-white/55 text-[11px] sm:text-xs italic">
                Nur noch wenige kostenlose Plätze verfügbar
              </p>
              </div>
            </div>
          </div>

          {/* Preview 3 + 4 vertikal rechts — nur ab md sichtbar */}
          <div className="hidden md:flex flex-col gap-4 justify-center">
            {[2, 3].map((index) => (
              <div
                key={index}
                onClick={() => setPreviewLightbox(index)}
                className="w-32 h-32 lg:w-40 lg:h-40 rounded-xl overflow-hidden cursor-pointer hover:scale-105 hover:shadow-xl hover:shadow-[#10B981]/30 transition-all border-2 border-[#10B981]/30 hover:border-[#10B981]/60"
              >
                <picture>
                  <source srcSet={previewImages[index]} type="image/webp" />
                  <img
                    src={previewImagesFallback[index]}
                    alt={`Preview ${index + 1}`}
                    loading="eager"
                    className="w-full h-full object-cover"
                  />
                </picture>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Live-Beispiel Badge - Klickbar mit Scroll zur ID-Card - DESKTOP ZENTRIERT */}
      <div className="max-w-4xl mx-auto px-4 mb-8 flex justify-center">
        <button
          onClick={() => {
            const idCard = document.querySelector('[data-showcase-idcard]');
            if (idCard) {
              idCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981]/10 hover:bg-[#10B981]/20 border border-[#10B981]/30 hover:border-[#10B981]/50 rounded-full transition-all duration-300 cursor-pointer group"
        >
          <div className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></div>
          <span className="text-[#10B981] font-semibold text-sm">Live-Beispiel</span>
          <span className="text-white/50 text-sm group-hover:text-white/70 transition-colors">— Sieh dir eine echte ID-Card an</span>
          <ArrowRight className="w-4 h-4 text-[#10B981]/50 group-hover:text-[#10B981] group-hover:translate-x-1 transition-all" />
        </button>
      </div>

      {/* Showcase ID-Card Section - Volle Breite wie im Dashboard */}
      <div className="py-12">
        
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#10B981]/30 border-t-[#10B981]"></div>
            <p className="text-white/60 mt-4">Lade Showcase...</p>
          </div>
        )}
        
        {error && (
          <div className="text-center py-20">
            <div className="inline-block p-8 bg-orange-500/10 border border-orange-500/30 rounded-2xl">
              <p className="text-orange-500 font-semibold mb-2">Showcase vorübergehend nicht verfügbar</p>
              <p className="text-white/60 text-sm mb-4">{error}</p>
              <Link 
                to="/welcome"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981] text-black rounded-lg hover:bg-[#059669] transition-all font-semibold"
              >
                Zur Anmeldung
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
        
        {!loading && !error && showcaseData && (
            <ShowcaseIDCard 
              showcaseData={showcaseData}
              limitToFirst={false}
            />
        )}
      </div>

      {/* CTA Section - Kompakter */}
      <div className="max-w-4xl mx-auto px-4 pt-8 pb-12 text-center">
        <div className="p-8 bg-gradient-to-br from-[#10B981]/10 to-black border-2 border-[#10B981]/40 rounded-3xl">
          <Zap className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-4">
            Bereit für deine eigene ID-Card?
          </h2>
          <p className="text-white/70 mb-6 text-lg">
            Erstelle deinen Avatar und generiere unbegrenzt fotorealistische Bilder von dir.
          </p>
          <button
            onClick={() => {
              navigate('/welcome');
              setTimeout(() => {
                const element = document.getElementById('wie-funktioniert');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 300);
            }}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#10B981] to-[#059669] text-black rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-[#10B981]/60 hover:scale-105 transition-all duration-300 cursor-pointer"
            data-testid="create-own-idcard-button"
          >
            <Camera className="w-6 h-6" />
            <span>Jetzt eigene ID-Card erstellen</span>
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Instagram Follow */}
      <div className="max-w-4xl mx-auto px-4 pb-8 text-center">
        <p className="text-white/50 text-sm mb-3">Folge uns für Inspiration</p>
        <a
          href="https://www.instagram.com/snapshotpro.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
          @snapshotpro.ai
        </a>
      </div>

      {/* Footer */}
      <div className="text-center py-8 border-t border-white/10">
        <p className="text-white/40 text-sm">
          © 2025 SNAPSHOT · KI-powered Avatar Platform
        </p>
      </div>

      {/* Scroll to Top Button - Für gesamte Seite */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 p-4 bg-[#10B981] hover:bg-[#059669] text-black rounded-full shadow-2xl transition-all z-50 animate-bounce hover:scale-110"
          title="Nach oben scrollen"
          data-testid="scroll-to-top-button"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}

      {/* Preview Lightbox */}
      {previewLightbox !== null && (
        <div 
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4"
          onClick={() => setPreviewLightbox(null)}
        >
          <button
            onClick={() => setPreviewLightbox(null)}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-white/10 transition-all z-10"
          >
            <X className="w-6 h-6" />
          </button>
          
          <img 
            src={previewImages[previewLightbox]} 
            alt={`Preview ${previewLightbox + 1}`}
            className="max-w-full max-h-[90vh] rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Navigation */}
          {previewImages.length > 1 && (
            <>
              {previewLightbox > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewLightbox(previewLightbox - 1);
                  }}
                  className="absolute left-4 w-12 h-12 flex items-center justify-center rounded-full bg-black/80 border-2 border-white/30 text-white hover:bg-white/10 transition-all"
                >
                  ←
                </button>
              )}
              
              {previewLightbox < previewImages.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewLightbox(previewLightbox + 1);
                  }}
                  className="absolute right-4 w-12 h-12 flex items-center justify-center rounded-full bg-black/80 border-2 border-white/30 text-white hover:bg-white/10 transition-all"
                >
                  →
                </button>
              )}
            </>
          )}
        </div>
      )}

    </div>
  );
};

export default SkipPage;
